import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { godrejShettigereService } from '../../services/godrejshettigereService';
import { Save, X, Plus, Trash2, UploadCloud } from 'lucide-react';
import { nanoid } from 'nanoid';

const GodrejShettigereContentManager = ({ setEditingGodrejShettigere, updatePageLastUpdated }) => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const content = await godrejShettigereService.getGodrejContent();
        setPageContent(content);
      } catch (error) {
        showError('Failed to load Godrej Shettigere page content: ' + error.message, 'error');
        setPageContent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [showError]);

  const handleSaveContent = async (sectionName, content) => {
    try {
      const updatedFullContent = {
        ...pageContent,
        [sectionName]: content
      };

      await godrejShettigereService.createOrUpdateGodrejContent(updatedFullContent);
      showError(`${sectionName} section updated successfully!`, 'success');
      const latestContent = await godrejShettigereService.getGodrejContent();
      setPageContent(latestContent);
      await updatePageLastUpdated('Godrej Shettigere');
    } catch (error) {
      showError(`Failed to update ${sectionName} section: ${error.message}`, 'error');
    }
  };

  const handleChange = (section, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      [section]: {
        ...prevContent[section],
        [field]: value
      }
    }));
  };

  const handleHeroSlideChange = (id, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      heroSection: {
        ...prevContent.heroSection,
        slides: prevContent.heroSection.slides.map(slide =>
          slide.id === id ? { ...slide, [field]: value } : slide
        )
      }
    }));
  };

  const handleAddSlide = () => {
    setPageContent(prevContent => {
      const newSlide = {
        id: nanoid(),
        image: '',
        alt: ''
      };
      return {
        ...prevContent,
        heroSection: {
          ...prevContent.heroSection,
          slides: [...prevContent.heroSection.slides, newSlide]
        }
      };
    });
  };

  const handleDeleteSlide = (id) => {
    setPageContent(prevContent => ({
      ...prevContent,
      heroSection: {
        ...prevContent.heroSection,
        slides: prevContent.heroSection.slides.filter(slide => slide.id !== id)
      }
    }));
  };

  const handleHeroKeyHighlightsChange = (index, value) => {
    setPageContent(prevContent => {
        const newHighlights = [...(prevContent?.heroSection?.keyHighlights || [])];
        newHighlights[index] = value;
        return {
            ...prevContent,
            heroSection: {
                ...prevContent.heroSection,
                keyHighlights: newHighlights
            }
        };
    });
  };

  const handleAddHeroHighlight = () => {
    setPageContent(prevContent => ({
        ...prevContent,
        heroSection: {
            ...prevContent.heroSection,
            keyHighlights: [...(prevContent?.heroSection?.keyHighlights || []), ""]
        }
    }));
  };

  const handleDeleteHeroHighlight = (index) => {
    setPageContent(prevContent => ({
        ...prevContent,
        heroSection: {
            ...prevContent.heroSection,
            keyHighlights: prevContent.heroSection.keyHighlights.filter((_, i) => i !== index)
        }
    }));
  };

  const handleSnapshotChange = (index, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      projectSnapshot: {
        ...prevContent.projectSnapshot,
        details: prevContent.projectSnapshot.details.map((detail, i) =>
          i === index ? { ...detail, [field]: value } : detail
        )
      }
    }));
  };

  const handleFloorPlanChange = (index, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      floorPlans: {
        ...prevContent.floorPlans,
        plans: prevContent.floorPlans.plans.map((plan, i) =>
          i === index ? { ...plan, [field]: value } : plan
        )
      }
    }));
  };

  const handleGalleryChange = (index, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      gallery: {
        ...prevContent.gallery,
        images: prevContent.gallery.images.map((image, i) =>
          i === index ? { ...image, [field]: value } : image
        )
      }
    }));
  };

  const handleAddGalleryImage = () => {
    setPageContent(prevContent => ({
      ...prevContent,
      gallery: {
        ...prevContent.gallery,
        images: [...prevContent.gallery.images, { url: '', category: '' }]
      }
    }));
  };

  const handleDeleteGalleryImage = (index) => {
    setPageContent(prevContent => ({
      ...prevContent,
      gallery: {
        ...prevContent.gallery,
        images: prevContent.gallery.images.filter((_, i) => i !== index)
      }
    }));
  };

  const handleImageUpload = async (file, section, index = null, field = null) => {
    if (!file) {
      showError('Please select a file to upload.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const newImageUrl = await godrejShettigereService.uploadImage(file);

      if (section === 'aboutProject') {
        handleChange('aboutProject', 'image', newImageUrl);
      } else if (section === 'locationMap') {
        handleChange('locationMap', 'image', newImageUrl);
      } else if (section === 'aboutDeveloper') {
        handleChange('aboutDeveloper', 'logo', newImageUrl);
      } else if (section === 'gallery' && index !== null && field !== null) {
        handleGalleryChange(index, field, newImageUrl);
      } else if (section === 'floorPlans' && index !== null && field !== null) {
        handleFloorPlanChange(index, field, newImageUrl);
      } else if (section === 'heroSection' && index !== null && field !== null) {
        const slideId = pageContent.heroSection.slides[index].id;
        handleHeroSlideChange(slideId, field, newImageUrl);
      }
      showError('Image uploaded successfully!', 'success');
    } catch (error) {
      showError('Failed to upload image: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-950 rounded-lg shadow-xl dark:shadow-2xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">Loading Godrej Shettigere editor...</p>
        </div>
      </div>
    );
  }

  if (!pageContent) {
    return (
      <div className="text-center py-10 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-xl shadow-lg">
        <p className="font-semibold text-xl">Error:</p>
        <p className="mt-2">Could not load Godrej Shettigere content for editing. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="mt-10 bg-white dark:bg-gray-900 p-4 md:p-8 rounded-2xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-800 animate-fade-in">
      <div className="flex justify-between items-center mb-8 border-b-4 pb-4 border-accent dark:border-gray-700">
        <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">Edit Godrej Shettigere Page Content</h2>
        <button
          onClick={() => setEditingGodrejShettigere(false)}
          className="flex items-center px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 text-sm font-medium shadow-sm"
        >
          <X className="w-5 h-5 mr-2" /> Close
        </button>
      </div>

      <div className="space-y-12">
        {/* Page Meta Title */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Page Meta Title</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="pageTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Page Title</label>
              <input
                type="text"
                id="pageTitle"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.pageTitle || ''}
                onChange={(e) => setPageContent(prev => ({ ...prev, pageTitle: e.target.value }))}
              />
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('pageTitle', pageContent.pageTitle)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Page Title
          </button>
        </div>

        {/* Hero Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Hero Section</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="heroTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title</label>
              <input
                type="text"
                id="heroTitle"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.title || ''}
                onChange={(e) => handleChange('heroSection', 'title', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="heroSubtitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subtitle</label>
              <input
                type="text"
                id="heroSubtitle"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.subtitle || ''}
                onChange={(e) => handleChange('heroSection', 'subtitle', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="bookingStatus" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Booking Status</label>
              <input
                type="text"
                id="bookingStatus"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.bookingStatus || ''}
                onChange={(e) => handleChange('heroSection', 'bookingStatus', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="bookingStatusHighlight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Booking Status Highlight</label>
              <input
                type="text"
                id="bookingStatusHighlight"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.bookingStatusHighlight || ''}
                onChange={(e) => handleChange('heroSection', 'bookingStatusHighlight', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="apartmentType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Apartment Type</label>
              <input
                type="text"
                id="apartmentType"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.apartmentType || ''}
                onChange={(e) => handleChange('heroSection', 'apartmentType', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Price</label>
              <input
                type="text"
                id="price"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.price || ''}
                onChange={(e) => handleChange('heroSection', 'price', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="priceSuffix" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Price Suffix</label>
              <input
                type="text"
                id="priceSuffix"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.priceSuffix || ''}
                onChange={(e) => handleChange('heroSection', 'priceSuffix', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="rera" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">RERA Number</label>
              <input
                type="text"
                id="rera"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.rera || ''}
                onChange={(e) => handleChange('heroSection', 'rera', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
              <input
                type="text"
                id="phoneNumber"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.heroSection?.phoneNumber || ''}
                onChange={(e) => handleChange('heroSection', 'phoneNumber', e.target.value)}
              />
            </div>
          </div>
          <div className="mt-8">
            <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-2">Key Highlights</h4>
            {pageContent?.heroSection?.keyHighlights?.map((highlight, index) => (
              <div key={index} className="flex items-center space-x-2 mb-3">
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                  value={highlight || ''}
                  onChange={(e) => handleHeroKeyHighlightsChange(index, e.target.value)}
                />
                <button
                  onClick={() => handleDeleteHeroHighlight(index)}
                  className="p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors duration-200"
                >
                  <Trash2 className="w-6 h-6" />
                </button>
              </div>
            ))}
            <button
              onClick={handleAddHeroHighlight}
              className="mt-2 flex items-center px-4 py-2 bg-green-200 dark:bg-green-300 text-gray-800  rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              <Plus className="w-5 h-5 mr-2" /> Add Highlight
            </button>
          </div>
          <button
            onClick={() => handleSaveContent('heroSection', pageContent.heroSection)}
            className="mt-8 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Hero Section
          </button>
          <div className="mt-10">
             <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Hero Slides</h4>
            {pageContent?.heroSection?.slides?.map((slide, index) => (
              <div key={slide.id} className="col-span-2 space-y-4 border-t pt-4 mt-4 border-gray-300 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-gray-800 dark:text-white">Slide {index + 1}</h4>
                  <button
                    onClick={() => handleDeleteSlide(slide.id)}
                    className="flex items-center text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors duration-200"
                  >
                    <Trash2 className="w-5 h-5 mr-1" /> Delete
                  </button>
                </div>
                {slide.image && (
                  <div className="mb-2">
                    <img src={slide.image} alt={`Slide ${slide.id}`} className="max-h-60 object-cover rounded-lg shadow-md" />
                  </div>
                )}
                <div>
                  <label htmlFor={`slide-image-${slide.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image URL</label>
                  <input
                    type="text"
                    id={`slide-image-${slide.id}`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={slide.image || ''}
                    onChange={(e) => handleHeroSlideChange(slide.id, 'image', e.target.value)}
                  />
                </div>
                <div className="flex items-center space-x-4">
                  <label htmlFor={`slide-upload-${slide.id}`} className="flex-grow flex items-center justify-start px-6 py-3 border  hover:bg-accent/30 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">
                    <UploadCloud className="w-5 h-5 mr-2 text-accent" />
                    Upload Image
                    <input
                      id={`slide-upload-${slide.id}`}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'heroSection', index, 'image')}
                    />
                  </label>
                </div>
              </div>
            ))}
            <div className="col-span-2 mt-4">
              <button
                onClick={handleAddSlide}
                className="flex items-center px-4 py-2 bg-green-200 dark:bg-green-300 text-gray-800  rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <Plus className="w-5 h-5 mr-2" /> Add New Slide
              </button>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('heroSection', pageContent.heroSection)}
            className="mt-8 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Hero Section
          </button>
        </div>

       {/* Project Snapshot Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Project Snapshot</h3>
          <div>
            <label htmlFor="projectSnapshotHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              id="projectSnapshotHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.projectSnapshot?.heading || ''}
              onChange={(e) => handleChange('projectSnapshot', 'heading', e.target.value)}
            /> 
          </div>
          <div>
            <label htmlFor="projectSnapshotSubheading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subheading</label>
            <input
              type="text"
              id="projectSnapshotSubheading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.projectSnapshot?.subheading || ''}
              onChange={(e) => handleChange('projectSnapshot', 'subheading', e.target.value)}
            /> 
          </div>
           <div>
            <label htmlFor="projectSnapshotAltHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Alternate Heading</label>
            <input
              type="text"
              id="projectSnapshotAltSubHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.projectSnapshot?.altsubheading || ''}
              onChange={(e) => handleChange('projectSnapshot', 'altsubheading', e.target.value)}
            /> 
            </div>
            <div className="mt-8 space-y-4">
            <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-2">Summary Details</h4>
            {pageContent?.projectSnapshot?.summary?.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Value</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                            value={item.value}
                            onChange={(e) => {
                                const newSummary = [...pageContent.projectSnapshot.summary];
                                newSummary[index].value = e.target.value;
                                handleChange('projectSnapshot', 'summary', newSummary);
                            }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Label</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                            value={item.label}
                            onChange={(e) => {
                                const newSummary = [...pageContent.projectSnapshot.summary];
                                newSummary[index].label = e.target.value;
                                handleChange('projectSnapshot', 'summary', newSummary);
                            }}
                        />
                    </div>
                </div>
            ))}
             <button
                onClick={() => {
                    const newSummary = [...(pageContent?.projectSnapshot?.summary || []), { value: '', label: '' }];
                    handleChange('projectSnapshot', 'summary', newSummary);
                }}
                className="mt-2 flex items-center px-4 py-2 bg-green-200 dark:bg-green-300 text-gray-800 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
            >
                <Plus className="w-5 h-5 mr-2" /> Add Summary Item
            </button>
        </div>

          {pageContent?.projectSnapshot?.details?.map((detail, index) => (
            <div key={index} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Detail Type</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                  value={detail.type}
                  onChange={(e) => handleSnapshotChange(index, 'type', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Detail Value</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                  value={detail.value}
                  onChange={(e) => handleSnapshotChange(index, 'value', e.target.value)}
                />
              </div>
            </div>
          ))}
          <button
            onClick={() => handleSaveContent('projectSnapshot', pageContent.projectSnapshot)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Project Snapshot
          </button>
        </div>
        {/* About Project Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">About Project</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="aboutHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
              <input
                type="text"
                id="aboutHeading"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.aboutProject?.heading || ''}
                onChange={(e) => handleChange('aboutProject', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="aboutImage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image</label>
              {pageContent?.aboutProject?.image && (
                <div className="mb-4">
                  <img src={pageContent.aboutProject.image} alt="About Project" className="max-h-60 object-cover rounded-lg shadow-md" />
                </div>
              )}
              <input
                type="text"
                id="aboutImage"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.aboutProject?.image || ''}
                onChange={(e) => handleChange('aboutProject', 'image', e.target.value)}
              />
              <div className="mt-2">
                <label htmlFor="aboutImageUpload" className="flex items-center justify-start px-6 py-3  hover:bg-accent/30 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">

                  <UploadCloud className="w-5 h-5 mr-2 text-accent" />

                  Upload Image
                  <input
                    id="aboutImageUpload"
                    type="file"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'aboutProject')}
                  />
                </label>
              </div>
            </div>
            <div>
              <label htmlFor="aboutText1" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Paragraph 1</label>
              <textarea
                id="aboutText1"
                rows="4"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.aboutProject?.text?.[0] || ''}
                onChange={(e) => {
                  const newText = [...pageContent.aboutProject.text];
                  newText[0] = e.target.value;
                  setPageContent(prev => ({
                    ...prev,
                    aboutProject: { ...prev.aboutProject, text: newText }
                  }));
                }}
              ></textarea>
            </div>
            <div>
              <label htmlFor="aboutText2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Paragraph 2</label>
              <textarea
                id="aboutText2"
                rows="4"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.aboutProject?.text?.[1] || ''}
                onChange={(e) => {
                  const newText = [...pageContent.aboutProject.text];
                  newText[1] = e.target.value;
                  setPageContent(prev => ({
                    ...prev,
                    aboutProject: { ...prev.aboutProject, text: newText }
                  }));
                }}
              ></textarea>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('aboutProject', pageContent.aboutProject)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save About Project
          </button>
        </div>

        {/* Floor Plans Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Floor Plans</h3>
          <div>
            <label htmlFor="floorPlansHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              id="floorPlansHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.floorPlans?.heading || ''}
              onChange={(e) => handleChange('floorPlans', 'heading', e.target.value)}
            />
          </div>
          {pageContent?.floorPlans?.plans?.map((plan, index) => (
            <div key={index} className="space-y-4 mb-6 p-6 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm">
              <h4 className="text-lg font-medium text-gray-800 dark:text-white">Plan {index + 1}</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">BHK</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                  value={plan.bhk}
                  onChange={(e) => handleFloorPlanChange(index, 'bhk', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Area</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                  value={plan.area}
                  onChange={(e) => handleFloorPlanChange(index, 'area', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image</label>
                {plan.image && (
                  <div className="mb-4">
                    <img src={plan.image} alt={`Floor Plan ${index + 1}`} className="max-h-60 object-cover rounded-lg shadow-md" />
                  </div>
                )}
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                  value={plan.image}
                  onChange={(e) => handleFloorPlanChange(index, 'image', e.target.value)}
                />
                <div className="mt-2">
                  <label htmlFor={`floorPlanUpload-${index}`} className="flex items-center justify-start px-6 py-3  hover:bg-accent/30 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">

                    <UploadCloud className="w-5 h-5 mr-2 text-accent" />

                    Upload Image
                    <input
                      id={`floorPlanUpload-${index}`}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'floorPlans', index, 'image')}
                    />
                  </label>
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={() => handleSaveContent('floorPlans', pageContent.floorPlans)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Floor Plans
          </button>
        </div>

        {/* Gallery Section - UPDATED */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Gallery</h3>
          <div>
            <label htmlFor="galleryHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              id="galleryHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.gallery?.heading || ''}
              onChange={(e) => handleChange('gallery', 'heading', e.target.value)}
            />
          </div>
          <div className="space-y-6">
            <h4 className="text-lg font-medium text-gray-800 dark:text-white">Interior</h4>
            {pageContent?.gallery?.images?.slice(0, 5).map((image, index) => (
              <div key={index} className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Image {index + 1}</label>
                  <button
                    onClick={() => handleDeleteGalleryImage(index)}
                    className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors duration-200"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                {image.url && (
                  <div className="mb-4">
                    <img src={image.url} alt={`Gallery Image ${index + 1}`} className="max-h-60 object-cover rounded-lg shadow-md" />
                  </div>
                )}
                <div>
                  <label htmlFor={`galleryImageUrl-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image URL</label>
                  <input
                    type="text"
                    id={`galleryImageUrl-${index}`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={image.url || ''}
                    onChange={(e) => handleGalleryChange(index, 'url', e.target.value)}
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor={`galleryImageCategory-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                  <input
                    type="text"
                    id={`galleryImageCategory-${index}`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={image.category || ''}
                    onChange={(e) => handleGalleryChange(index, 'category', e.target.value)}
                  />
                </div>
                <div className="mt-2">
                  <label htmlFor={`galleryImageUpload-${index}`} className="flex items-center justify-start px-6 py-3  hover:bg-accent/30 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">
                    <UploadCloud className="w-5 h-5 mr-2 text-accent" />
                    Upload Image
                    <input
                      id={`galleryImageUpload-${index}`}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'gallery', index, 'url')}
                    />
                  </label>
                </div>
              </div>
            ))}
             <button
            onClick={() => handleSaveContent('gallery', pageContent.gallery)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Interior Images
          </button>
            <h4 className="text-lg font-medium text-gray-800 dark:text-white mt-8">Clubhouse</h4>
            {pageContent?.gallery?.images?.slice(5, 10).map((image, index) => (
              <div key={index + 5} className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Image {index + 6}</label>
                  <button
                    onClick={() => handleDeleteGalleryImage(index + 5)}
                    className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors duration-200"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                {image.url && (
                  <div className="mb-4">
                    <img src={image.url} alt={`Gallery Image ${index + 6}`} className="max-h-60 object-cover rounded-lg shadow-md" />
                  </div>
                )}
                <div>
                  <label htmlFor={`galleryImageUrl-${index + 5}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image URL</label>
                  <input
                    type="text"
                    id={`galleryImageUrl-${index + 5}`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={image.url || ''}
                    onChange={(e) => handleGalleryChange(index + 5, 'url', e.target.value)}
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor={`galleryImageCategory-${index + 5}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                  <input
                    type="text"
                    id={`galleryImageCategory-${index + 5}`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={image.category || ''}
                    onChange={(e) => handleGalleryChange(index + 5, 'category', e.target.value)}
                  />
                </div>
                <div className="mt-2">
                  <label htmlFor={`galleryImageUpload-${index + 5}`} className="flex items-center justify-start px-6 py-3  hover:bg-accent/30 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">
                    <UploadCloud className="w-5 h-5 mr-2 text-accent" />
                    Upload Image
                    <input
                      id={`galleryImageUpload-${index + 5}`}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'gallery', index + 5, 'url')}
                    />
                  </label>
                </div>
              </div>
            ))}
             <button
            onClick={() => handleSaveContent('gallery', pageContent.gallery)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Clubhouse Images
          </button>
            <h4 className="text-lg font-medium text-gray-800 dark:text-white mt-8">Playing Area</h4>
            {pageContent?.gallery?.images?.slice(10, 15).map((image, index) => (
              <div key={index + 10} className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Image {index + 11}</label>
                  <button
                    onClick={() => handleDeleteGalleryImage(index + 10)}
                    className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors duration-200"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                {image.url && (
                  <div className="mb-4">
                    <img src={image.url} alt={`Gallery Image ${index + 11}`} className="max-h-60 object-cover rounded-lg shadow-md" />
                  </div>
                )}
                <div>
                  <label htmlFor={`galleryImageUrl-${index + 10}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image URL</label>
                  <input
                    type="text"
                    id={`galleryImageUrl-${index + 10}`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={image.url || ''}
                    onChange={(e) => handleGalleryChange(index + 10, 'url', e.target.value)}
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor={`galleryImageCategory-${index + 10}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                  <input
                    type="text"
                    id={`galleryImageCategory-${index + 10}`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={image.category || ''}
                    onChange={(e) => handleGalleryChange(index + 10, 'category', e.target.value)}
                  />
                </div>
                <div className="mt-2">
                  <label htmlFor={`galleryImageUpload-${index + 10}`} className="flex items-center justify-start px-6 py-3  hover:bg-accent/30 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">
                    <UploadCloud className="w-5 h-5 mr-2 text-accent" />
                    Upload Image
                    <input
                      id={`galleryImageUpload-${index + 10}`}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'gallery', index + 10, 'url')}
                    />
                  </label>
                </div>
              </div>
            ))}
            <button
              onClick={() => handleSaveContent('gallery', pageContent.gallery)}
              className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
            >
              <Save className="w-5 h-5 mr-2" /> Save Playing Area Images
            </button>
            <div className="col-span-2 mt-4">
              <button
                onClick={handleAddGalleryImage}
                className="flex items-center px-4 py-2 bg-green-200 dark:bg-green-300 text-gray-800  rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <Plus className="w-5 h-5 mr-2" /> Add New Image
              </button>
            </div>
          </div>
        </div>

        {/* Key Highlights Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Key Highlights</h3>
          <div>
            <label htmlFor="keyHighlightsHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              id="keyHighlightsHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.keyHighlights?.heading || ''}
              onChange={(e) => handleChange('keyHighlights', 'heading', e.target.value)}
            />
          </div>
          <div className="space-y-6">
            {pageContent?.keyHighlights?.points?.map((point, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={point.title}
                    onChange={(e) => {
                      const newPoints = [...pageContent.keyHighlights.points];
                      newPoints[index].title = e.target.value;
                      setPageContent(prev => ({
                        ...prev,
                        keyHighlights: { ...prev.keyHighlights, points: newPoints }
                      }));
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subtitle</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                    value={point.subtitle}
                    onChange={(e) => {
                      const newPoints = [...pageContent.keyHighlights.points];
                      newPoints[index].subtitle = e.target.value;
                      setPageContent(prev => ({
                        ...prev,
                        keyHighlights: { ...prev.keyHighlights, points: newPoints }
                      }));
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => handleSaveContent('keyHighlights', pageContent.keyHighlights)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Key Highlights
          </button>
        </div>

        {/* Location & Map Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Location & Map</h3>
          <div>
            <label htmlFor="locationMapHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              id="locationMapHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.locationMap?.heading || ''}
              onChange={(e) => handleChange('locationMap', 'heading', e.target.value)}
            />
          </div>
          <div className="space-y-6">
            <div>
              <label htmlFor="locationImage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location Map Image</label>
              {pageContent?.locationMap?.image && (
                <div className="mb-4">
                  <img src={pageContent.locationMap.image} alt="Location Map" className="max-h-60 object-cover rounded-lg shadow-md" />
                </div>
              )}
              <input
                type="text"
                id="locationImage"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.locationMap?.image || ''}
                onChange={(e) => handleChange('locationMap', 'image', e.target.value)}
              />
              <div className="mt-2">
                <label htmlFor="locationImageUpload" className="flex items-center justify-start px-6 py-3  hover:bg-accent/30 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">

                  <UploadCloud className="w-5 h-5 mr-2 text-accent" />
                  Upload Image
                  <input
                    id="locationImageUpload"
                    type="file"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'locationMap')}
                  />
                </label>
              </div>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('locationMap', pageContent.locationMap)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Location Map
          </button>
        </div>

        {/* About Developer Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">About Developer</h3>
          <div>
            <label htmlFor="aboutDeveloperHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              id="aboutDeveloperHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.aboutDeveloper?.heading || ''}
              onChange={(e) => handleChange('aboutDeveloper', 'heading', e.target.value)}
            />
          </div>
          <div className="space-y-6">
            <div>
              <label htmlFor="developerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
              <input
                type="text"
                id="developerName"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.aboutDeveloper?.name || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'name', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="developerLogo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Logo</label>
              {pageContent?.aboutDeveloper?.logo && (
                <div className="mb-4">
                  <img src={pageContent.aboutDeveloper.logo} alt="Developer Logo" className="max-h-60 object-contain rounded-lg shadow-md" />
                </div>
              )}
              <input
                type="text"
                id="developerLogo"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.aboutDeveloper?.logo || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'logo', e.target.value)}
              />
              <div className="mt-2">
                <label htmlFor="developerLogoUpload" className="flex items-center justify-start px-6 py-3  hover:bg-accent/30 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900  transition-all duration-300 cursor-pointer">

                  <UploadCloud className="w-5 h-5 mr-2 text-accent" />
                  Upload Logo
                  <input
                    id="developerLogoUpload"
                    type="file"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'aboutDeveloper')}
                  />
                </label>
              </div>
            </div>
            <div>
              <label htmlFor="developerText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
              <textarea
                id="developerText"
                rows="6"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.aboutDeveloper?.text || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'text', e.target.value)}
              ></textarea>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('aboutDeveloper', pageContent.aboutDeveloper)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save About Developer
          </button>
        </div>

        {/* Pre-book Section */}
        <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Pre-Book Section</h3>
          <div>
            <label htmlFor="prebookHeading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              id="prebookHeading"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
              value={pageContent?.prebookSection?.heading || ''}
              onChange={(e) => handleChange('prebookSection', 'heading', e.target.value)}
            />
          </div>
          <div className="space-y-6">
            <div>
              <label htmlFor="prebookText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Text</label>
              <textarea
                id="prebookText"
                rows="4"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-950 transition duration-300"
                value={pageContent?.prebookSection?.text || ''}
                onChange={(e) => handleChange('prebookSection', 'text', e.target.value)}
              ></textarea>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('prebookSection', pageContent.prebookSection)}
            className="mt-6 flex items-center px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/90 transition-all duration-300 font-bold transform hover:scale-105"
          >
            <Save className="w-5 h-5 mr-2" /> Save Pre-Book Section
          </button>
        </div>
      </div>
    </div>
  );
};

export default GodrejShettigereContentManager;